import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Search, 
  Trash2, 
  Mail, 
  MailOpen, 
  Download, 
  LogOut,
  Menu,
  X,
  Home,
  MessageSquare,
  Moon,
  Sun,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square,
  RefreshCw,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple math captcha generator
const generateCaptcha = () => {
  const num1 = Math.floor(Math.random() * 10) + 1;
  const num2 = Math.floor(Math.random() * 10) + 1;
  const operators = ['+', '-', '×'];
  const operator = operators[Math.floor(Math.random() * 3)];
  
  let answer;
  switch (operator) {
    case '+':
      answer = num1 + num2;
      break;
    case '-':
      answer = num1 - num2;
      break;
    case '×':
      answer = num1 * num2;
      break;
    default:
      answer = num1 + num2;
  }
  
  return {
    question: `${num1} ${operator} ${num2} = ?`,
    answer: answer.toString()
  };
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Login security
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockEndTime, setLockEndTime] = useState(null);
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState("");
  
  // Data states
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, nouveau: 0, lu: 0 });
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [activeView, setActiveView] = useState("dashboard"); // dashboard or messages
  const [selectedMessage, setSelectedMessage] = useState(null);
  
  // Filters and pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const limit = 10;

  // Check authentication and lock status on mount
  useEffect(() => {
    const auth = localStorage.getItem("ttwood_admin_auth");
    const attempts = parseInt(localStorage.getItem("ttwood_login_attempts") || "0");
    const lockTime = localStorage.getItem("ttwood_lock_time");
    
    if (auth === "true") {
      setIsAuthenticated(true);
    }
    
    setLoginAttempts(attempts);
    
    if (lockTime) {
      const lockEnd = new Date(lockTime);
      if (lockEnd > new Date()) {
        setIsLocked(true);
        setLockEndTime(lockEnd);
      } else {
        // Lock expired, reset
        localStorage.removeItem("ttwood_lock_time");
        localStorage.setItem("ttwood_login_attempts", "0");
        setLoginAttempts(0);
      }
    }
    
    setIsLoading(false);
  }, []);

  // Countdown timer for lock
  useEffect(() => {
    if (!isLocked || !lockEndTime) return;
    
    const interval = setInterval(() => {
      if (new Date() >= lockEndTime) {
        setIsLocked(false);
        setLockEndTime(null);
        setLoginAttempts(0);
        localStorage.removeItem("ttwood_lock_time");
        localStorage.setItem("ttwood_login_attempts", "0");
        setCaptcha(generateCaptcha());
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isLocked, lockEndTime]);

  // Fetch data when authenticated
  const fetchContacts = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: limit,
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      if (searchQuery) {
        params.append("search", searchQuery);
      }
      
      const response = await axios.get(`${API}/admin/contacts?${params}`);
      setContacts(response.data.contacts || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalContacts(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      toast.error("Erreur lors du chargement des messages");
    }
  }, [isAuthenticated, currentPage, statusFilter, searchQuery, sortBy, sortOrder]);

  const fetchStats = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchContacts();
    fetchStats();
  }, [fetchContacts, fetchStats]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => {
      fetchContacts();
      fetchStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchContacts, fetchStats]);

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (isLocked) {
      toast.error("Trop de tentatives. Veuillez patienter.");
      return;
    }
    
    // Verify captcha first
    if (captchaInput !== captcha.answer) {
      toast.error("Captcha incorrect");
      setCaptcha(generateCaptcha());
      setCaptchaInput("");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        localStorage.setItem("ttwood_admin_auth", "true");
        localStorage.setItem("ttwood_login_attempts", "0");
        setIsAuthenticated(true);
        setLoginAttempts(0);
        toast.success("Connexion réussie");
      }
    } catch (error) {
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      localStorage.setItem("ttwood_login_attempts", newAttempts.toString());
      
      if (newAttempts >= 3) {
        // Lock for 15 minutes
        const lockEnd = new Date(Date.now() + 15 * 60 * 1000);
        setIsLocked(true);
        setLockEndTime(lockEnd);
        localStorage.setItem("ttwood_lock_time", lockEnd.toISOString());
        toast.error("Compte verrouillé pendant 15 minutes");
      } else {
        toast.error(`Mot de passe incorrect (${3 - newAttempts} essai(s) restant(s))`);
      }
      
      setCaptcha(generateCaptcha());
      setCaptchaInput("");
      setPassword("");
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("ttwood_admin_auth");
    setIsAuthenticated(false);
    navigate("/dashboard_tt38");
  };

  // Update contact status
  const updateContactStatus = async (contactId, status) => {
    try {
      await axios.patch(`${API}/admin/contacts/${contactId}`, { status });
      toast.success(`Message marqué comme ${status === "lu" ? "lu" : "non lu"}`);
      fetchContacts();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Delete contact
  const deleteContact = async (contactId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce message ?")) return;
    
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`);
      toast.success("Message supprimé");
      fetchContacts();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    if (selectedContacts.length === 0) return;
    if (!window.confirm(`Supprimer ${selectedContacts.length} message(s) ?`)) return;
    
    try {
      await axios.post(`${API}/admin/contacts/bulk-delete`, selectedContacts);
      toast.success(`${selectedContacts.length} message(s) supprimé(s)`);
      setSelectedContacts([]);
      fetchContacts();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleBulkMarkRead = async () => {
    if (selectedContacts.length === 0) return;
    
    try {
      await axios.post(`${API}/admin/contacts/bulk-update`, selectedContacts, {
        params: { status: "lu" }
      });
      toast.success(`${selectedContacts.length} message(s) marqué(s) comme lu(s)`);
      setSelectedContacts([]);
      fetchContacts();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ["Nom", "Email", "Téléphone", "Type de projet", "Message", "Statut", "Date"];
    const csvContent = [
      headers.join(","),
      ...contacts.map(c => [
        `"${c.nom || c.name || ''}"`,
        `"${c.email || ''}"`,
        `"${c.telephone || c.phone || ''}"`,
        `"${c.type_projet || c.project_type || ''}"`,
        `"${(c.message || '').replace(/"/g, '""')}"`,
        `"${c.status || ''}"`,
        `"${new Date(c.created_at).toLocaleDateString('fr-FR')}"`
      ].join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `ttwood_contacts_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success("Export CSV téléchargé");
  };

  // Toggle select contact
  const toggleSelectContact = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  // Select all on current page
  const toggleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(c => c.id));
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get remaining lock time
  const getRemainingLockTime = () => {
    if (!lockEndTime) return "";
    const remaining = Math.max(0, Math.ceil((lockEndTime - new Date()) / 1000));
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-deep-oak border-t-transparent"></div>
      </div>
    );
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-warm-cream'}`}>
        <div className={`w-full max-w-md p-8 rounded-2xl shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="text-center mb-8">
            <h1 className={`font-heading text-3xl font-bold ${darkMode ? 'text-white' : 'text-deep-oak'}`}>
              TT Wood Admin
            </h1>
            <p className={`mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Gestion des messages
            </p>
          </div>
          
          {isLocked ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Compte verrouillé
              </h3>
              <p className={`mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Trop de tentatives de connexion échouées.
              </p>
              <p className={`mt-4 text-2xl font-mono ${darkMode ? 'text-white' : 'text-deep-oak'}`}>
                {getRemainingLockTime()}
              </p>
              <p className={`mt-2 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Réessayez dans quelques minutes
              </p>
            </div>
          ) : (
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Mot de passe administrateur
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:ring-2 focus:ring-deep-oak focus:border-transparent`}
                  placeholder="Entrez le mot de passe"
                  required
                  data-testid="admin-password-input"
                />
              </div>
              
              {/* Captcha */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Vérification de sécurité
                </label>
                <div className={`flex items-center gap-4 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <span className={`text-xl font-mono font-bold ${darkMode ? 'text-white' : 'text-deep-oak'}`}>
                    {captcha.question}
                  </span>
                  <input
                    type="text"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    className={`flex-1 px-4 py-2 rounded-lg border ${
                      darkMode 
                        ? 'bg-gray-600 border-gray-500 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-deep-oak focus:border-transparent`}
                    placeholder="Réponse"
                    required
                    data-testid="captcha-input"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setCaptcha(generateCaptcha());
                      setCaptchaInput("");
                    }}
                    className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                    title="Nouveau captcha"
                  >
                    <RefreshCw size={20} />
                  </button>
                </div>
              </div>

              {loginAttempts > 0 && (
                <p className="text-orange-500 text-sm text-center">
                  {3 - loginAttempts} essai(s) restant(s) avant verrouillage
                </p>
              )}
              
              <button
                type="submit"
                className="w-full btn-primary py-3"
                data-testid="admin-login-btn"
              >
                Se connecter
              </button>
            </form>
          )}
          
          <div className="mt-6 text-center">
            <a href="/" className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} hover:underline`}>
              ← Retour au site
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}>
      {/* Mobile menu button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className={`lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
        data-testid="mobile-menu-btn"
      >
        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full z-40 transition-all duration-300
        ${sidebarOpen ? 'w-64' : 'w-20'}
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className={`font-heading text-xl font-bold ${sidebarOpen ? 'block' : 'hidden'}`}>
              TT Wood
            </h2>
            {!sidebarOpen && <span className="text-2xl">🪵</span>}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            <button 
              onClick={() => setActiveView("dashboard")}
              className={`w-full flex items-center gap-3 p-3 rounded-lg ${activeView === "dashboard" ? (darkMode ? 'bg-gray-700' : 'bg-deep-oak/10') : ''} hover:${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
            >
              <Home size={20} />
              {sidebarOpen && <span className="font-medium">Tableau de bord</span>}
            </button>
            <button 
              onClick={() => setActiveView("messages")}
              className={`w-full flex items-center gap-3 p-3 rounded-lg ${activeView === "messages" ? (darkMode ? 'bg-gray-700' : 'bg-deep-oak/10') : ''} hover:${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
            >
              <MessageSquare size={20} />
              {sidebarOpen && (
                <div className="flex items-center justify-between flex-1">
                  <span>Messages</span>
                  {stats.nouveau > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                      {stats.nouveau}
                    </span>
                  )}
                </div>
              )}
            </button>
          </nav>

          {/* Footer */}
          <div className={`p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} space-y-2`}>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg hover:${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
              data-testid="dark-mode-toggle"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              {sidebarOpen && <span>{darkMode ? 'Mode clair' : 'Mode sombre'}</span>}
            </button>
            <button
              onClick={handleLogout}
              className={`w-full flex items-center gap-3 p-3 rounded-lg text-red-500 hover:bg-red-50 ${darkMode && 'hover:bg-red-900/20'}`}
              data-testid="logout-btn"
            >
              <LogOut size={20} />
              {sidebarOpen && <span>Déconnexion</span>}
            </button>
          </div>

          {/* Collapse button */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`hidden lg:flex absolute -right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-white'} shadow-lg items-center justify-center`}
          >
            {sidebarOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'} p-4 lg:p-8`}>
        {/* Stats cards - always visible */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 mt-12 lg:mt-0">
          <div 
            onClick={() => setActiveView("messages")}
            className={`p-6 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm cursor-pointer hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total messages</p>
                <p className="text-3xl font-bold mt-1">{stats.total}</p>
              </div>
              <div className={`p-3 rounded-full ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                <MessageSquare className="text-blue-500" size={24} />
              </div>
            </div>
          </div>
          
          <div 
            onClick={() => { setActiveView("messages"); setStatusFilter("nouveau"); }}
            className={`p-6 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm cursor-pointer hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Nouveaux</p>
                <p className="text-3xl font-bold mt-1 text-orange-500">{stats.nouveau}</p>
              </div>
              <div className={`p-3 rounded-full ${darkMode ? 'bg-orange-900/30' : 'bg-orange-100'}`}>
                <Mail className="text-orange-500" size={24} />
              </div>
            </div>
          </div>
          
          <div 
            onClick={() => { setActiveView("messages"); setStatusFilter("lu"); }}
            className={`p-6 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm cursor-pointer hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Lus</p>
                <p className="text-3xl font-bold mt-1 text-green-500">{stats.lu}</p>
              </div>
              <div className={`p-3 rounded-full ${darkMode ? 'bg-green-900/30' : 'bg-green-100'}`}>
                <MailOpen className="text-green-500" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Message detail modal */}
        {selectedMessage && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedMessage(null)}>
            <div 
              className={`max-w-2xl w-full rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl max-h-[80vh] overflow-auto`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-start`}>
                <div>
                  <h3 className="font-heading text-xl font-bold">{selectedMessage.nom || selectedMessage.name}</h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedMessage.email}</p>
                </div>
                <button onClick={() => setSelectedMessage(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className={`text-xs uppercase ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Téléphone</p>
                    <p className="font-medium">{selectedMessage.telephone || selectedMessage.phone || '-'}</p>
                  </div>
                  <div>
                    <p className={`text-xs uppercase ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Type de projet</p>
                    <p className="font-medium">{selectedMessage.type_projet || selectedMessage.project_type || '-'}</p>
                  </div>
                  <div>
                    <p className={`text-xs uppercase ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Date</p>
                    <p className="font-medium">{formatDate(selectedMessage.created_at)}</p>
                  </div>
                  <div>
                    <p className={`text-xs uppercase ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Statut</p>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      selectedMessage.status === 'nouveau' 
                        ? 'bg-orange-100 text-orange-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {selectedMessage.status === 'nouveau' ? 'Nouveau' : 'Lu'}
                    </span>
                  </div>
                </div>
                <div>
                  <p className={`text-xs uppercase mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Message</p>
                  <p className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} whitespace-pre-wrap`}>
                    {selectedMessage.message}
                  </p>
                </div>
              </div>
              <div className={`p-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex gap-3`}>
                <button
                  onClick={() => {
                    updateContactStatus(selectedMessage.id, selectedMessage.status === 'nouveau' ? 'lu' : 'nouveau');
                    setSelectedMessage(null);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
                >
                  {selectedMessage.status === 'nouveau' ? <MailOpen size={18} /> : <Mail size={18} />}
                  {selectedMessage.status === 'nouveau' ? 'Marquer comme lu' : 'Marquer comme non lu'}
                </button>
                <button
                  onClick={() => {
                    deleteContact(selectedMessage.id);
                    setSelectedMessage(null);
                  }}
                  className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters and actions */}
        <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm mb-6`}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 w-full lg:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher par nom, email..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-gray-50 border-gray-200'
                } focus:ring-2 focus:ring-deep-oak focus:border-transparent`}
                data-testid="search-input"
              />
            </div>

            <div className="flex flex-wrap gap-3 items-center">
              {/* Status filter */}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 rounded-lg border ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-gray-50 border-gray-200'
                }`}
                data-testid="status-filter"
              >
                <option value="all">Tous les statuts</option>
                <option value="nouveau">Nouveaux</option>
                <option value="lu">Lus</option>
              </select>

              {/* Refresh */}
              <button
                onClick={() => { fetchContacts(); fetchStats(); }}
                className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                title="Actualiser"
                data-testid="refresh-btn"
              >
                <RefreshCw size={20} />
              </button>

              {/* Export */}
              <button
                onClick={exportToCSV}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                }`}
                data-testid="export-btn"
              >
                <Download size={18} />
                <span className="hidden sm:inline">Export CSV</span>
              </button>

              {/* Bulk actions */}
              {selectedContacts.length > 0 && (
                <>
                  <button
                    onClick={handleBulkMarkRead}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
                    data-testid="bulk-read-btn"
                  >
                    <MailOpen size={18} />
                    <span className="hidden sm:inline">Marquer lu ({selectedContacts.length})</span>
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 size={18} />
                    <span className="hidden sm:inline">Supprimer ({selectedContacts.length})</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Messages table */}
        <div className={`rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <tr>
                  <th className="p-4 text-left">
                    <button onClick={toggleSelectAll} className="p-1">
                      {selectedContacts.length === contacts.length && contacts.length > 0 
                        ? <CheckSquare size={18} /> 
                        : <Square size={18} />
                      }
                    </button>
                  </th>
                  <th className="p-4 text-left font-medium">Nom</th>
                  <th className="p-4 text-left font-medium hidden md:table-cell">Email</th>
                  <th className="p-4 text-left font-medium hidden lg:table-cell">Téléphone</th>
                  <th className="p-4 text-left font-medium hidden xl:table-cell">Type</th>
                  <th className="p-4 text-left font-medium">Statut</th>
                  <th className="p-4 text-left font-medium hidden sm:table-cell">Date</th>
                  <th className="p-4 text-left font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {contacts.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-gray-500">
                      Aucun message trouvé
                    </td>
                  </tr>
                ) : (
                  contacts.map((contact) => (
                    <tr 
                      key={contact.id} 
                      className={`${
                        contact.status === 'nouveau' 
                          ? darkMode ? 'bg-orange-900/10' : 'bg-orange-50' 
                          : ''
                      } hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                    >
                      <td className="p-4">
                        <button onClick={() => toggleSelectContact(contact.id)} className="p-1">
                          {selectedContacts.includes(contact.id) 
                            ? <CheckSquare size={18} className="text-deep-oak" /> 
                            : <Square size={18} />
                          }
                        </button>
                      </td>
                      <td className="p-4 cursor-pointer" onClick={() => setSelectedMessage(contact)}>
                        <div className="font-medium">{contact.nom || contact.name}</div>
                        <div className="text-sm text-gray-500 md:hidden">{contact.email}</div>
                        <div className="text-xs text-gray-400 mt-1 line-clamp-2 max-w-xs">
                          {contact.message}
                        </div>
                      </td>
                      <td className="p-4 hidden md:table-cell cursor-pointer" onClick={() => setSelectedMessage(contact)}>{contact.email}</td>
                      <td className="p-4 hidden lg:table-cell cursor-pointer" onClick={() => setSelectedMessage(contact)}>{contact.telephone || contact.phone || '-'}</td>
                      <td className="p-4 hidden xl:table-cell cursor-pointer" onClick={() => setSelectedMessage(contact)}>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-100'
                        }`}>
                          {contact.type_projet || contact.project_type}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          contact.status === 'nouveau' 
                            ? 'bg-orange-100 text-orange-700' 
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {contact.status === 'nouveau' ? 'Nouveau' : 'Lu'}
                        </span>
                      </td>
                      <td className="p-4 hidden sm:table-cell text-sm">
                        {formatDate(contact.created_at)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateContactStatus(contact.id, contact.status === 'nouveau' ? 'lu' : 'nouveau')}
                            className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                            title={contact.status === 'nouveau' ? 'Marquer comme lu' : 'Marquer comme non lu'}
                            data-testid={`toggle-status-${contact.id}`}
                          >
                            {contact.status === 'nouveau' ? <MailOpen size={18} /> : <Mail size={18} />}
                          </button>
                          <button
                            onClick={() => deleteContact(contact.id)}
                            className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Supprimer"
                            data-testid={`delete-${contact.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={`flex items-center justify-between p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <p className="text-sm text-gray-500">
                Page {currentPage} sur {totalPages} ({totalContacts} messages)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded-lg ${
                    currentPage === 1 
                      ? 'opacity-50 cursor-not-allowed' 
                      : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                  data-testid="prev-page"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded-lg ${
                    currentPage === totalPages 
                      ? 'opacity-50 cursor-not-allowed' 
                      : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                  data-testid="next-page"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
